import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ExternalLink, LogOut } from 'lucide-react';

import { ConfirmDialog } from '../../components/core/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/LanguageContext';
import { PLAN_NAME_KEYS } from '../../i18n';
import { cancelSubscription } from '../../api/subscription.api';
import type { UserSubscription } from '../../api/subscription.api';
import { LOGIN_PATH, PRICING_PATH } from '../../constants/routes';
import { daysUntil, formatBytes, formatDate, formatNumber } from '../../utils/format';

/** A used/limit bar that turns amber then rose as the quota runs out. */
function UsageBar({
  label,
  used,
  limit,
  formatValue = formatNumber,
}: Readonly<{
  label: string;
  used: number;
  limit: number;
  formatValue?: (n: number) => string;
}>) {
  // A zero limit would divide to Infinity; treat it as fully consumed instead.
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 100;
  let barColor = 'bg-violet-500';
  if (pct >= 90) barColor = 'bg-rose-500';
  else if (pct >= 70) barColor = 'bg-amber-500';

  return (
    <div className="mt-1">
      <div className="flex justify-between text-xs text-violet-400 mb-1">
        <span>{label}</span>
        <span>
          {formatValue(used)} / {formatValue(limit)}
        </span>
      </div>
      <div className="h-1.5 bg-violet-500/20 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

interface SubscriptionPageProps {
  subscription: UserSubscription | null;
  loading: boolean;
  error: string | null;
  onCancelSuccess: () => void;
}

export function SubscriptionPage({
  subscription,
  loading,
  error,
  onCancelSuccess,
}: Readonly<SubscriptionPageProps>) {
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!user) {
    return (
      <div className="text-center py-6">
        <p className="text-xs text-slate-500 mb-3">{t('subscription.signInPrompt')}</p>
        <button
          onClick={() => navigate(LOGIN_PATH)}
          className="text-xs text-violet-400 font-semibold hover:underline"
        >
          {t('common.signIn')}
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="text-xs text-slate-400 text-center py-6">{t('common.loading')}</div>;
  }

  if (error || !subscription) {
    return (
      <div className="text-xs text-rose-500 text-center py-6">
        {error ?? t('subscription.loadError')}
      </div>
    );
  }

  const sub = subscription;
  const daysLeft = daysUntil(sub.periodEnd);

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelSubscription();
      setConfirmOpen(false);
      onCancelSuccess();
    } catch (err: unknown) {
      // Kept on screen inside the dialog — this used to be written to a discarded
      // state slot, so a failed cancellation told the user nothing at all.
      setCancelError((err as { message?: string }).message ?? t('subscription.cancelFailed'));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-3 max-w-xl">
      <div className="rounded-xl border-2 border-violet-500/30 bg-violet-500/10 p-3">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-xs font-bold text-violet-200">
              {t('subscription.planLabel', { plan: t(PLAN_NAME_KEYS[sub.plan]) })}
            </p>
            <p className="text-xs text-violet-400 mt-0.5">{t(`status.${sub.status}`)}</p>
          </div>
          <div className="text-right">
            <p
              className={`text-xs font-bold ${daysLeft <= 5 ? 'text-rose-400' : 'text-violet-300'}`}
            >
              {daysLeft === 0
                ? t('common.expired')
                : t('subscription.daysLeft', { days: daysLeft })}
            </p>
            <p className="text-xs text-violet-400">{formatDate(sub.periodEnd)}</p>
          </div>
        </div>

        <UsageBar
          label={t('subscription.postsUsed')}
          used={sub.postsPublished}
          limit={sub.limits.postsPerMonth}
        />
        <UsageBar
          label={t('subscription.generationsUsed')}
          used={sub.aiGenerations}
          limit={sub.limits.aiGenerationsPerMonth}
        />
        <UsageBar
          label={t('subscription.storageUsed')}
          used={sub.mediaBytes}
          limit={sub.limits.mediaStorageMb * 1024 * 1024}
          formatValue={formatBytes}
        />
      </div>

      {daysLeft <= 7 && daysLeft > 0 && sub.status === 'active' && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-300">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-amber-400" />
          {daysLeft === 1
            ? t('subscription.expiryWarningOne')
            : t('subscription.expiryWarning', { days: daysLeft })}
        </div>
      )}

      <button
        onClick={() => navigate(PRICING_PATH)}
        className="flex items-center justify-center gap-1.5 w-full rounded-xl bg-violet-600 py-2 text-xs font-semibold text-white hover:bg-violet-500 transition-colors"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        {t('subscription.viewUpgrade')}
      </button>

      {sub.plan !== 'free_trial' && sub.status === 'active' && (
        <button
          onClick={() => setConfirmOpen(true)}
          disabled={cancelling}
          className="w-full rounded-xl border border-rose-500/40 py-2 text-xs font-semibold text-rose-400 hover:bg-rose-500/10 transition-colors"
        >
          {cancelling ? t('subscription.cancelling') : t('subscription.cancelAction')}
        </button>
      )}

      <ConfirmDialog
        open={confirmOpen}
        // Dismissing clears the error so a retry does not open onto a stale failure.
        onOpenChange={(next) => {
          setConfirmOpen(next);
          if (!next) setCancelError(null);
        }}
        variant="destructive"
        title={t('subscription.confirmTitle')}
        description={t('subscription.confirmBody')}
        confirmLabel={t('subscription.confirmYes')}
        cancelLabel={t('subscription.confirmNo')}
        loading={cancelling}
        error={cancelError}
        onConfirm={handleCancel}
      />

      <div className="rounded-lg bg-slate-700/50 border border-slate-700 p-3 space-y-1">
        <p className="text-xs font-semibold text-slate-300 mb-1">{t('subscription.limitsTitle')}</p>
        <p className="text-xs text-slate-400">
          {t('subscription.limitBrands')}{' '}
          <strong className="text-slate-200">{sub.limits.brands}</strong>
        </p>
        <p className="text-xs text-slate-400">
          {t('subscription.limitAccounts')}{' '}
          <strong className="text-slate-200">{sub.limits.connectedAccounts}</strong>
        </p>
        <p className="text-xs text-slate-400">
          {t('subscription.limitHorizon')}{' '}
          <strong className="text-slate-200">
            {t('subscription.days', { days: sub.limits.scheduleHorizonDays })}
          </strong>
        </p>
        <p className="text-xs text-slate-400">
          {t('subscription.postsRemaining')}{' '}
          <strong className="text-slate-200">{formatNumber(sub.postsRemaining)}</strong>
        </p>
      </div>

      <button
        onClick={() => signOut()}
        className="flex items-center gap-1.5 w-full rounded-xl border border-slate-700 py-2 text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-700 transition-colors justify-center"
      >
        <LogOut className="h-3.5 w-3.5" /> {t('common.signOut')}
      </button>
    </div>
  );
}
